import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { Skeleton, Typography } from 'antd';

import CourseAlerts from '@/components/shared/NegativeAlerts/Course';
import OfferCard from '@/components/shared/OfferCard';
import PageBackgroundImage from '@/components/shared/PageBackgroundImage';
import ProgressStatus from '@/components/shared/ProgressStatus';
import CourseTerm from '@/components/shared/Term';
import Wrapper from '@/components/shared/Wrapper';
import { useFacebookPixel } from '@/hooks/facebook';
import { useLog } from '@/hooks/useLog';
import { blockedStatus, enStatus } from '@/interfaces/course';
import { enTermOrigin } from '@/interfaces/term';
import { useGetCourseQuery, useGetUpsellQuery } from '@/services/course';
import styled, { StyledProp, css } from '@/styled';

import Author from './Author';
import Integrations from './Integrations';
import KeepLearning from './KeepLearning';
import CourseTabs from './Tabs';

const Course: React.FC = (props: StyledProp) => {
  const { hash } = useParams();
  const { data } = useGetCourseQuery(hash);
  const { data: upsellData, isLoading } = useGetUpsellQuery(hash);
  const { activity } = useLog();

  useFacebookPixel(data);

  useEffect(() => {
    if (!data) return;
    const message = blockedStatus.includes(data.status)
      ? `Visualizou o curso ${data.title} bloqueado(a)`
      : `Visualizou o curso ${data.title}`;
    activity({
      context: 'viewCourse',
      additionalData: {
        message,
        courseId: data.id,
        course_hash: data.hash,
        status: data.status
      }
    });
  }, [data, activity]);

  return (
    <div className={props.className}>
      <CourseTerm origin={enTermOrigin.course} />
      <PageBackgroundImage
        src={data?.customizations.course_newcard_background || data?.customizations?.image_cover}
        alt='banner de fundo da tela de curso'
      />

      <Wrapper className='wrapper'>
        <Typography.Title level={1} ellipsis className='title' id='course-title'>
          {data?.title ?? <Skeleton.Input size='small' />}
        </Typography.Title>
        <ProgressStatus className='progress' />
        <KeepLearning courseStatus={data?.status} />
        <CourseAlerts />
        <div className='content'>
          <CourseTabs
            disable_comments={data?.disable_comments}
            enabled_talk_to_producer={data?.enable_talk_to_producer || data?.blocked_by === enStatus.NOT_PURCHASED}
          />
          <div className='side-content'>
            <Integrations />
            <Author />
            <OfferCard data={upsellData} isLoading={isLoading} />
          </div>
        </div>
      </Wrapper>
    </div>
  );
};

export default styled(Course)(
  ({ theme }) => css`
    position: relative;
    overflow: hidden;
    min-height: 100vh;
    > .wrapper {
      z-index: 2;
      position: relative;
      display: flex;
      flex-direction: column;

      .courseTitle {
        max-width: 500px;
        color: ${theme.colorTextBase};
      }

      .ant-skeleton {
        width: 100%;
      }
      .progress {
        margin-bottom: ${theme.space.pxToRem(64)};
      }

      .ant-skeleton {
        border-radius: ${theme.borderRadius}px;
        height: fit-content !important;
      }

      h1.ant-typography {
        margin-top: ${theme.space.pxToRem(74)};
        margin-bottom: ${theme.space.pxToRem(20)};
      }
    }

    .content {
      display: grid;
      margin-top: ${theme.space.pxToRem(64)};
      grid-template-columns: auto 308px;
      max-width: 100%;
      position: relative;
      overflow: hidden;

      .ant-tabs {
        width: 100%;
        max-width: 100%;
        overflow: hidden;
      }

      ${theme.breakpoints.down('md')} {
        grid-template-columns: auto;
        grid-row-gap: ${theme.space.pxToRem(64)};
      }
    }

    .side-content {
      display: flex;
      flex-direction: column;
      gap: ${theme.space.pxToRem(24)};
    }
  `
);
